import { useEffect, useState } from 'react';
import { ButtonData } from '../../../../../components/buttonGroup/ButtonGroup';
import { WaitlistLegend } from '../../../../../components/generic';
import { FilterField, FilterForm } from '../../../../../components/tables/filterForms/FilterForm';
import { FormSelect } from '../../../../../components/tables/filterForms/FormSelect';
import SFCTable from '../../../../../components/tables/SFCTable';
import { useDataContext } from '../../../../../context';
import { useToast } from '../../../../../context/ToastContext';
import { useListFiltersInUrl } from '../../../../../hooks';
import { Scopes, User } from '../../../../../services/endpoints/people/user';
import { useGetAllWaitlistsQuery } from '../../../../../services/endpoints/schedule/waitlist';
import { getCurrentUser } from '../../../../../services/helper';
import { Waitlist } from '../../../../../types/waitlist.type';
import { waitlistColumns } from '../../../../../utils/consts/columnDefs/waitlistColumns';

const defaultScope = 'pending';
const currentUser = getCurrentUser();

export const userCanEditCreateDeleteView_Waitlist = (user: User | null) => {
  if (
    user?.isSuper ||
    user?.isSupremeAdmin ||
    user?.isAdmin ||
    user?.isGlobal ||
    user?.isLocationAdmin
  ) {
    return true;
  }

  return false;
};

export const WaitlistView = () => {
  const { addToast } = useToast();
  const { locationOptions } = useDataContext();
  const [filters, setFilters] = useListFiltersInUrl({ scope: defaultScope });
  const { data, error, isLoading, isFetching } = useGetAllWaitlistsQuery(filters, {
    refetchOnMountOrArgChange: true
  });
  const [lessons, setLessons] = useState<Waitlist[]>([]);
  const [scopes, setScopes] = useState<Scopes>({});
  const [buttons, setButtons] = useState<ButtonData[][]>();
  const isGlobalUser = currentUser?.isGlobal;

  const filterFormFields: FilterField[] = [
    {
      name: 'id',
      label: 'ID',
      type: 'number',
      value: (filters.data?.filterBy?.find((f) => f.field === 'id')?.value as string) ?? ''
    },
    {
      name: 'course_title',
      label: 'Session',
      value:
        (filters.data?.filterBy?.find((f) => f.field === 'course_title')?.value as string) ?? ''
    },
    {
      name: 'child_full_name',
      label: 'Child',
      value:
        (filters.data?.filterBy?.find((f) => f.field === 'child_full_name')?.value as string) ?? ''
    },
    {
      name: 'parent_email',
      label: 'Parent Email Address',
      value:
        (filters.data?.filterBy?.find((f) => f.field === 'parent_email')?.value as string) ?? ''
    },
    {
      name: 'location_id',
      operator: 'equals',
      element: (
        <FormSelect
          name="location_id"
          label="Location"
          options={locationOptions}
          selected_Value={locationOptions.find(
            (option) =>
              String(option.value) ===
              String(filters.data?.filterBy?.find((f) => f.field === 'location_id')?.value)
          )}
        />
      )
    }
  ];

  if (!isGlobalUser) {
    // remove location filter
    filterFormFields.splice(-1, 1);
  }

  useEffect(() => {
    if (!error && !isLoading && data && data.results) {
      if (data.scopes) {
        setScopes(
          data.scopes.reduce((acc: Scopes, curr: Scopes) => ({ ...acc, ...curr }), {} as Scopes)
        );
        setButtons(
          data.scopes.map((sMap) =>
            Object.entries(sMap).map(([scope, title]) => {
              let scope_count = 0;
              if (data.scope_counts != null) {
                scope_count = data.scope_counts[scope] ?? 0;
              }
              return {
                title: `${title} (${scope_count})`,
                key: scope
              };
            })
          )
        );
      }
    } else if (error) {
      addToast('Error while loading data', 'error');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, error, isLoading]);

  useEffect(() => {
    if (data && data.results) {
      setLessons(data.results);
    }
  }, [data]);

  const setFilterBy = (filterBy: { operator: string; field: string; value: string }[]) => {
    setFilters((prev) => ({
      ...prev,
      data: {
        ...prev!.data,
        filterBy
      }
    }));
  };

  return (
    <div className="py-2">
      <SFCTable
        name={`${scopes[filters?.scope!] ?? ''} Waitlists`}
        columns={waitlistColumns}
        data={lessons}
        count={data?.count}
        defaultScope={filters.scope ?? defaultScope}
        scopes={buttons}
        isLoading={isLoading}
        isFetching={isFetching}
        filters={filters?.data?.filterBy}
        indexDownloadPath="waitlists"
        sidebars={[<WaitlistLegend />]}
        filterForm={
          <FilterForm
            fields={filterFormFields}
            onApplyFilters={setFilterBy}
          />
        }
        onFiltersChanged={setFilters}
      />
    </div>
  );
};
